<script setup lang="ts">
import { computed, onMounted, reactive, ref, type Ref } from 'vue';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CButtonGroup, { type CButtonGroupItem } from '@/components/shared/CButtonGroup';
import CFileInput, { type CFileInputRef } from '@/components/shared/CFileInput';
import CPagination from '@/components/shared/CPagination';
import CPreview from '@/components/shared/CPreview.vue';
import { getDefaultMasks, getUserMasks } from '@/api/masks';
import type { MaskDto } from '@/api/typings/mask';
import { MaskType } from '@/utils/enums/maskType.enum';
import {
    MASK_UPLOAD_ACCEPT,
    RASTER_MASK_UPLOAD_ACCEPT,
    isSvgMask,
    uploadAndRegisterMask
} from '@/utils/maskUtils';

const PAGE_SIZE = 35;

type MaskSource = 'customily' | 'uploaded';

const props = withDefaults(
    defineProps<{
        type?: MaskType;
        disableSvg?: boolean;
        multiple?: boolean;
        /** Replaces the Customily shapes fetched from the server, for features shipping their own built-in set. */
        presets?: MaskDto[];
        title?: string;
        accept?: string;
        /** Rejected before uploading, so oversized files never reach the user's library. */
        maxKbSize?: number;
    }>(),
    { type: MaskType.Mask }
);

const emit = defineEmits<{
    (event: 'select', mask: MaskDto): void;
    (event: 'select-multiple', masks: MaskDto[]): void;
    (event: 'cancel'): void;
    (event: 'error', message: string): void;
}>();

const sources: CButtonGroupItem<MaskSource>[] = [
    { label: 'Customily', value: 'customily' },
    { label: 'Uploaded', value: 'uploaded' }
];

const model = reactive({
    filters: [] as MaskSource[],
    defaultMasks: [] as MaskDto[],
    userMasks: [] as MaskDto[],
    userMasksTotal: 0,
    page: 1,
    loading: false,
    uploading: false,
    // Kept as DTOs in pick order, so the selection survives page and filter changes
    selected: [] as MaskDto[]
});

const fileInputRef: Ref<CFileInputRef | undefined> = ref();

const accept = computed(
    () => props.accept ?? (props.disableSvg ? RASTER_MASK_UPLOAD_ACCEPT : MASK_UPLOAD_ACCEPT)
);
// Filters are inclusive, so none picked and both picked mean the same thing: show everything
const isFiltered = (source: MaskSource) => !model.filters.length || model.filters.includes(source);
const showUserMasks = computed(() => isFiltered('uploaded'));
const showDefaultMasks = computed(() => isFiltered('customily'));

const userMasksCount = computed(() => (showUserMasks.value ? model.userMasksTotal : 0));
const totalMaskCount = computed(
    () => userMasksCount.value + (showDefaultMasks.value ? model.defaultMasks.length : 0)
);

// Unfiltered it is one flat list of the user's masks followed by the Customily ones: the defaults
// start filling the page where the uploaded ones run out and spill onto the next ones
const masks = computed(() => {
    const userMasks = showUserMasks.value ? model.userMasks : [];
    if (!showDefaultMasks.value) return userMasks;

    const firstDefault = Math.max(0, (model.page - 1) * PAGE_SIZE - userMasksCount.value);
    return [
        ...userMasks,
        ...model.defaultMasks.slice(firstDefault, firstDefault + PAGE_SIZE - userMasks.length)
    ];
});
const isEmpty = computed(() => !model.loading && !masks.value.length);
const emptyMessage = computed(() => {
    if (!showDefaultMasks.value) {
        return "You haven't uploaded any shape yet. Upload one and it will be available on every template.";
    }
    if (!showUserMasks.value) return 'There are no Customily shapes available yet.';
    return 'There are no shapes available yet. Upload one and it will be available on every template.';
});

const loadDefaultMasks = async () => {
    model.defaultMasks = props.presets ?? (await getDefaultMasks(props.type));
};

const loadUserMasks = async (page: number) => {
    const { masks: userMasks, totalCount } = await getUserMasks(page, PAGE_SIZE, props.type);
    model.userMasks = userMasks;
    model.userMasksTotal = totalCount;
    model.page = page;
};

const withLoading = async (loading: Promise<unknown>) => {
    model.loading = true;
    try {
        await loading;
    } finally {
        model.loading = false;
    }
};

const onPageChange = (page: number) => withLoading(loadUserMasks(page));

const onFiltersChange = (filters: MaskSource[]) => {
    model.filters = filters;
    if (model.page !== 1) onPageChange(1);
};

const isDisabled = (mask: MaskDto) => props.disableSvg && isSvgMask(mask.path);

const selectionOrder = (mask: MaskDto) => model.selected.findIndex((selected) => selected.id === mask.id) + 1;

const toggleSelection = (mask: MaskDto) => {
    const index = selectionOrder(mask) - 1;
    if (index >= 0) {
        model.selected.splice(index, 1);
    } else {
        model.selected.push(mask);
    }
};

const onMaskSelected = (mask: MaskDto) => {
    if (isDisabled(mask)) return;

    if (props.multiple) {
        toggleSelection(mask);
        return;
    }
    emit('select', mask);
};

const onConfirm = () => {
    if (!model.selected.length) return;
    emit('select-multiple', [...model.selected]);
};

const onUploadClicked = () => fileInputRef.value?.openFileUploader();

// The mask picker keeps the message it always had
const uploadFailed = (error: unknown) =>
    emit(
        'error',
        (error as Error | undefined)?.message ||
            (props.type === MaskType.TextShape ? 'The shape could not be uploaded' : 'The mask could not be uploaded')
    );

const onFileUpload = async (files: File[]) => {
    if (!files?.length) return;

    const oversized = props.maxKbSize ? files.filter((file) => file.size > props.maxKbSize! * 1024) : [];
    if (oversized.length) {
        emit('error', `${oversized.map((file) => file.name).join(', ')} exceeds the ${props.maxKbSize}KB limit`);
        return;
    }

    model.uploading = true;
    try {
        if (!props.multiple) {
            emit('select', await uploadAndRegisterMask(files[0], props.type));
            return;
        }

        // One by one so a failing file keeps the ones already registered selected
        for (const file of files) {
            try {
                model.selected.push(await uploadAndRegisterMask(file, props.type));
            } catch (error) {
                uploadFailed(error);
            }
        }
        // The uploads already went through and stay selected, so a failed refresh isn't an upload error
        await withLoading(loadUserMasks(1)).catch((error) => {
            console.error(error);
            emit('error', 'The shapes were uploaded but the list could not be refreshed');
        });
    } catch (error) {
        uploadFailed(error);
    } finally {
        model.uploading = false;
    }
};

onMounted(() => withLoading(Promise.all([loadDefaultMasks(), loadUserMasks(1)])));
</script>

<template>
    <c-modal
        :value="true"
        :hide-footer="!multiple"
        size="lg"
        :title="title ?? (multiple ? 'Select or Upload Shapes' : 'Select or Upload a Shape')"
        @closed="emit('cancel')"
    >
        <div class="mask-selector">
            <span class="filter-label">Filter Shapes</span>
            <c-button-group
                :value="model.filters"
                :items="sources"
                type="checkbox"
                size="sm"
                pill
                separate
                no-icon
                @input="onFiltersChange"
            />

            <div class="mask-grid">
                <button
                    type="button"
                    class="mask-tile upload-tile"
                    :disabled="model.uploading"
                    @click="onUploadClicked"
                >
                    <font-awesome-icon v-if="model.uploading" class="fa-spin" :icon="['fas', 'spinner-third']" />
                    <template v-else>
                        <font-awesome-icon :icon="['far', 'arrow-up-from-bracket']" />
                        <span>Upload</span>
                    </template>
                </button>

                <button
                    v-for="mask of masks"
                    :key="mask.id"
                    type="button"
                    class="mask-tile"
                    :class="{ disabled: isDisabled(mask), selected: multiple && selectionOrder(mask) }"
                    :title="isDisabled(mask) ? 'SVG masks cannot be used with an initial image position' : mask.name"
                    @click="onMaskSelected(mask)"
                >
                    <c-preview :src="mask.thumbnailPath" :alt="mask.name" hide-wrapper max-height="100%" />
                    <span v-if="multiple && selectionOrder(mask)" class="selection-badge">
                        {{ selectionOrder(mask) }}
                    </span>
                </button>
            </div>

            <p v-if="isEmpty" class="empty-message">{{ emptyMessage }}</p>

            <c-pagination
                v-if="totalMaskCount > PAGE_SIZE"
                :value="model.page"
                :total-count="totalMaskCount"
                :page-count="PAGE_SIZE"
                @input="onPageChange"
            />

            <c-file-input
                ref="fileInputRef"
                class="d-none"
                :value="[]"
                :accept="accept"
                :multiple="multiple"
                :display-preview="false"
                @input="onFileUpload"
            />
        </div>

        <template v-if="multiple" #modal-footer>
            <span class="selection-count mr-auto">{{ model.selected.length }} selected</span>
            <c-button variant="secondary" text="Cancel" @click="emit('cancel')" />
            <c-button text="Confirm" :disabled="!model.selected.length || model.uploading" @click="onConfirm" />
        </template>
    </c-modal>
</template>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.mask-selector {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.filter-label {
    color: get-color('gray-500');
    font-size: 0.875rem;
}

.mask-grid {
    display: grid;
    grid-template-columns: repeat(9, minmax(0, 1fr));
    gap: 0.5rem;
}

.mask-tile {
    position: relative;
    aspect-ratio: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    padding: 0.5rem;
    background: get-color('white');
    border: 1px solid get-color('gray-200');
    border-radius: 0.25rem;
    font-size: 0.75rem;
    color: get-color('gray-600');

    &:hover:not(.disabled) {
        border-color: get-color('purple');
    }

    &.disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    &.selected {
        border: 2px solid get-color('purple');
    }

    img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }
}

.selection-badge {
    position: absolute;
    top: 0.125rem;
    right: 0.125rem;
    min-width: 1rem;
    padding: 0 0.25rem;
    border-radius: 0.5rem;
    background: get-color('purple');
    color: get-color('white');
    font-size: 0.625rem;
    line-height: 1rem;
}

.selection-count {
    color: get-color('gray-500');
    font-size: 0.875rem;
}

.upload-tile:disabled {
    cursor: progress;
}

.empty-message {
    margin: 0;
    color: get-color('gray-500');
    text-align: center;
}
</style>
